import { CalendlyExpert } from "@/features/experts/types/CalendlyExpert";
import { ExpertRole } from "@/features/experts/types/ExpertRole";
import { ChevronRightIcon } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { VenereBox } from "../../shared/ui";
import { useVenereChatSessions, useVenereCreateSession } from "../hooks";
import { VenereChatSession } from "../types/VenereChatSession";
import { getBoxVariant, getRoleDisplayString, getStatusInfo } from "../utils/chatPreviewUtils";

const VenereChatPreview: React.FC<{
    expert: CalendlyExpert | null
}> = ({
    expert,
}) => {
        const [isLocalLoading, setIsLocalLoading] = useState(false);
        const { data: chatSessions, isLoading, isFetching, refetch } = useVenereChatSessions();

        const { mutate, isError, isSuccess, data } = useVenereCreateSession(
            expert?.id || '',
            expert?.role || '',
        );

        const navigate = useNavigate();

        // Early return if expert is null (AFTER all hooks)
        if (!expert) {
            return null;
        }

        // Check for existing session OR newly created session
        // Use cached data if available, even during refetch
        const expertSession = chatSessions?.find(
            (session: VenereChatSession) => session.expert_id === expert.id,
        ) || (isSuccess && data ? data : null);

        const boxVariant = getBoxVariant(expertSession);
        const statusInfo = getStatusInfo(expertSession);
        const roleDisplayString = getRoleDisplayString(expert.role);

        // Show loading indicator only during initial load (no cached data)
        const showInitialLoading = isLoading && !chatSessions;
        // Show subtle loading indicator during refetch (when we have cached data)
        const showRefetchLoading = isFetching && chatSessions;

        const handleClick = async () => {
            if (isFetching || isLocalLoading) {
                return;
            }
            
            setIsLocalLoading(true);
            
            try {
                // Only refetch if we don't have cached data or if it's stale
                if (!chatSessions || isFetching) {
                    await refetch();
                }
                
                const expertSession = chatSessions?.find(
                    (session: VenereChatSession) => session.expert_id === expert.id,
                );

                if (expertSession) {
                    // Navigate to existing session
                    navigate(`/venere/chat/${expertSession.id}`);
                } else {
                    if (!isFetching) {
                        mutate();
                    }
                }
            } catch (error) {
                // Handle error silently or add proper error handling
            } finally {
                setIsLocalLoading(false);
            }
        };

        if (isSuccess && data) {
            // TODO: Navigate to new session
            navigate(`/venere/chat/${data.id}`);
        }

        return (
            <VenereBox 
                variant={boxVariant} 
                onClick={handleClick} 
                className="relative cursor-pointer hover:opacity-90 transition-all duration-200"
            >
                {/* Initial Loading Overlay - Only show when no cached data */}
                {showInitialLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-lg z-10">
                        <div className="flex flex-col items-center gap-2">
                            <div className="w-6 h-6 border-2 border-venere-royal border-t-transparent rounded-full animate-spin"></div>
                            <div className="text-xs text-venere-royal">Caricamento...</div>
                        </div>
                    </div>
                )}

                {/* Local Loading Overlay - During click actions */}
                {isLocalLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-lg z-10">
                        <div className="flex flex-col items-center gap-2">
                            <div className="w-6 h-6 border-2 border-venere-royal border-t-transparent rounded-full animate-spin"></div>
                            <div className="text-xs text-venere-royal">Caricamento...</div>
                        </div>
                    </div>
                )}
                
                {/* Status indicator with subtle loading */}
                <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color} flex items-center gap-1`}>
                    {showRefetchLoading && (
                        <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
                    )}
                    {statusInfo.text}
                </div>
                
                <div className="w-full rounded-lg p-2 flex items-center gap-2">
                    <img 
                        className={`w-[70px] h-[70px] rounded-[20px] ${expert.role === ExpertRole.Admin ? 'bg-white' : ''}`}
                        src={expert.calendly_data?.calendly_user?.avatar_url || "/images/assets/LOGO_Nero.svg"} 
                        alt={`${expert.data?.name || 'Expert'} avatar`}
                        onError={(e) => {
                            // Fallback to default avatar if image fails to load
                            e.currentTarget.src = "/images/assets/LucioPoletti.png";
                        }}
                    />
                    <div className="w-full flex items-center justify-between">
                        <div className="flex flex-col">
                            {expert.role !== ExpertRole.Admin && <div className="venere-text-secondary">{roleDisplayString}</div>}
                            <div className="venere-text-primary">
                                {expert.role === ExpertRole.Admin ? getRoleDisplayString(ExpertRole.Admin) : `${expert.data?.name || 'Unknown'} ${expert.data?.family_name || ''}`}
                            </div>
                        </div>
                        <ChevronRightIcon color="var(--venere-royal)" />
                    </div>
                </div>
            </VenereBox>
        )
    }

export default VenereChatPreview
